import { LitElement, html, css, svg } from 'lit'
import { brandLogo } from './brand-logo.js'

// Login, on the BrandExtract design system's sign-in treatment (shared with the
// support app): a dark eggplant hero with the mascot resting on a white card.
// Ziggy authenticates through Google Workspace SSO only, so the card carries the
// single Google action rather than the support app's email/password form.
class PageLogin extends LitElement {
  static styles = css`
    /* Full-bleed dark hero. Fixed so it paints over app-root's <main> box; the
       page has no nav when unauthenticated. */
    :host {
      position: fixed; inset: 0; overflow: auto; z-index: 0;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      background: var(--gradient-hero); padding: 32px 16px;
      font-family: var(--font-sans); color: var(--eggplant-500);
    }
    .wrap { width: min(460px, 94vw); }

    .masthead { display: flex; align-items: center; justify-content: center; gap: 16px; margin-bottom: 6px; }
    .masthead .logo { color: var(--white); display: block; }
    .masthead .logo svg { height: 40px; width: auto; }
    .masthead .tag {
      font-family: var(--font-eyebrow); font-size: 16px; font-weight: 700; letter-spacing: 0.2em;
      text-transform: uppercase; color: var(--chartreuse-500); padding-left: 16px;
      border-left: 1px solid rgba(255,255,255,0.28); position: relative; top: 6px;
    }

    .mascot { display: flex; justify-content: center; }
    .mascot img { width: 100%; height: auto; display: block; margin-bottom: -35px;
                  position: relative; z-index: 0; left: -10px; }

    .card {
      background: var(--white); border-radius: 14px; box-shadow: var(--shadow-card);
      padding: 40px 40px 32px; position: relative; z-index: 1;
    }
    h1 { margin: 0 0 6px; font-size: 28px; font-weight: 800; letter-spacing: -0.02em; color: var(--eggplant-500); }
    .sub { margin: 0 0 26px; font-size: 15px; color: var(--neutral-600); }

    /* Google SSO is Ziggy's only way in, so it takes the chartreuse CTA rather
       than the outline treatment it carries in the support app's card. */
    a.google {
      width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
      font-family: var(--font-sans); font-weight: 600; font-size: 16px; line-height: 24px;
      padding: 12px 24px; border-radius: 0; text-decoration: none; cursor: pointer;
      background: var(--chartreuse-500); color: var(--eggplant-500);
      box-sizing: border-box; transition: background-color .18s ease;
    }
    a.google:hover { background: var(--chartreuse-600); }

    .dev { text-align: center; margin: 18px 0 0; font-size: 14px; }
    .dev a { color: var(--sky-500); font-weight: 600; text-decoration: none; }
    .dev a:hover { text-decoration: underline; }

    .foot { text-align: center; margin-top: 22px; font-size: 12.5px; color: rgba(255,255,255,0.55); }

    .error { background: var(--white); color: #B4232B; box-shadow: inset 0 0 0 1px #B4232B;
             padding: 10px 14px; margin: 0 0 18px; font-size: 14px; }
  `

  get _error() {
    const reason = new URLSearchParams(window.location.search).get('error')
    if (reason === 'domain') return 'Please sign in with your @brandextract.com Google account.'
    if (reason === 'signin') return 'Sign-in failed. Please try again.'
    return null
  }

  get _isLocal() {
    return ['localhost', '127.0.0.1'].includes(window.location.hostname)
  }

  _googleMark() {
    return svg`<svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.89 2.68-6.62z"/><path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1z"/><path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"/></svg>`
  }

  render() {
    return html`
      <div class="wrap">
        <div class="masthead">
          <span class="logo">${brandLogo(40)}</span>
          <span class="tag">Ziggy</span>
        </div>
        <div class="mascot">
          <img src="/images/login-mascot.svg" alt="BrandExtract fortune teller mascot" />
        </div>
        <div class="card">
          <h1>Sign in</h1>
          <p class="sub">Review the time Ziggy found and send it to Workamajig.</p>

          ${this._error ? html`<div class="error">${this._error}</div>` : ''}

          <a class="google" href="/auth/google">${this._googleMark()} Sign in with Google</a>

          ${this._isLocal ? html`
            <p class="dev"><a href="/auth/dev-login">Continue as dev user →</a></p>
          ` : ''}
        </div>
        <p class="foot">© 2026 BrandExtract · Ziggy</p>
      </div>
    `
  }
}

customElements.define('page-login', PageLogin)
